/**
 * Custom Tailored Size & Measurement Form Component
 * Hidden by default on page load and for predefined sizes (XS, S, M, L, XL, XXL, XXXL).
 * Shown immediately when "Custom Size" is selected.
 * When Custom Size is selected, all 4 measurements are mandatory for Add to Cart, Buy It Now, and Wishlist.
 * When a predefined size is selected, form is hidden and validation does not block purchase actions.
 * Attaches measurements as native Shopify line-item properties.
 */
if (!customElements.get('custom-measurement-form')) {
  class CustomMeasurementForm extends HTMLElement {
    constructor() {
      super();
      this.onSizeChange = this.onSizeChange.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.syncHiddenProperties = this.syncHiddenProperties.bind(this);
      this.onActionAttempt = this.onActionAttempt.bind(this);
    }

    get inputs() {
      return this.querySelectorAll('.custom-measurement-input');
    }

    get hiddenInputs() {
      return this.querySelectorAll('input[data-hidden-property]');
    }

    get optionalLabel() {
      return this.querySelector('.custom-measurement-optional');
    }

    get errorElement() {
      return this.querySelector('.custom-measurement-error');
    }

    connectedCallback() {
      // Listen to input changes on the measurement fields
      this.inputs.forEach((input) => {
        input.removeEventListener('input', this.onInputChange);
        input.removeEventListener('change', this.onInputChange);
        input.addEventListener('input', this.onInputChange);
        input.addEventListener('change', this.onInputChange);
      });

      // Listen to change and click events on section and variant pickers
      const section = this.closest('section') || (this.dataset.sectionId ? document.getElementById(`shopify-section-${this.dataset.sectionId}`) : null) || document;
      section.removeEventListener('change', this.onSizeChange);
      section.removeEventListener('click', this.onSizeChange);
      section.addEventListener('change', this.onSizeChange);
      section.addEventListener('click', this.onSizeChange);

      // Global capturing listener to block Add to Cart, Buy It Now, and Wishlist if validation fails for Custom Size
      document.removeEventListener('click', this.onActionAttempt, true);
      document.addEventListener('click', this.onActionAttempt, true);

      // Initial check of selected size state
      this.checkCurrentSize();

      // Mutation observer to handle dynamic updates to variant selections
      if (window.MutationObserver) {
        if (this.observer) this.observer.disconnect();
        this.observer = new MutationObserver(() => {
          if (this._isChecking) return;
          this._isChecking = true;
          this.checkCurrentSize();
          this._isChecking = false;
        });

        const variantContainer = this.closest('.product__info-container') || (this.dataset.sectionId ? document.getElementById(`shopify-section-${this.dataset.sectionId}`) : null);
        if (variantContainer) {
          this.observer.observe(variantContainer, {
            childList: false,
            subtree: true,
            attributes: true,
            attributeFilter: ['checked', 'selected', 'aria-checked']
          });
        }
      }
    }

    disconnectedCallback() {
      const section = this.closest('section') || (this.dataset.sectionId ? document.getElementById(`shopify-section-${this.dataset.sectionId}`) : null) || document;
      section.removeEventListener('change', this.onSizeChange);
      section.removeEventListener('click', this.onSizeChange);
      document.removeEventListener('click', this.onActionAttempt, true);
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    isCustomTailoredValue(val) {
      if (!val || typeof val !== 'string') return false;
      const clean = val.trim().toLowerCase();
      return clean.includes('custom');
    }

    isCustomSelected() {
      const selectedSize = this.getSelectedSizeValue();
      if (selectedSize) {
        return this.isCustomTailoredValue(selectedSize);
      }
      return false;
    }

    getSelectedSizeValue() {
      const section = this.closest('section') || (this.dataset.sectionId ? document.getElementById(`shopify-section-${this.dataset.sectionId}`) : null) || document;

      // 1. Check checked radio button specifically for Size option
      const sizeRadioChecked = section.querySelector(
        'fieldset.option-name-size input[type="radio"]:checked, fieldset[name*="size" i] input[type="radio"]:checked, input[name="Size"]:checked, input[name="size"]:checked, input[name*="size" i]:checked'
      );
      if (sizeRadioChecked && sizeRadioChecked.value) {
        return sizeRadioChecked.value;
      }

      // 2. Check all checked radio buttons in variant pickers
      const checkedRadios = section.querySelectorAll('input[type="radio"]:checked');
      for (const radio of checkedRadios) {
        const name = (radio.name || '').toLowerCase();
        const fieldset = radio.closest('fieldset');
        const legend = fieldset ? (fieldset.querySelector('legend')?.textContent || '').toLowerCase() : '';
        const fieldsetClass = fieldset ? (fieldset.className || '').toLowerCase() : '';

        if (name.includes('size') || legend.includes('size') || fieldsetClass.includes('size') || this.isCustomTailoredValue(radio.value)) {
          return radio.value;
        }
      }

      // 3. Check dropdown selects for Size option
      const sizeSelect = section.querySelector(
        'fieldset.option-name-size select, select[name="Size"], select[name="size"], select[name*="size" i]'
      );
      if (sizeSelect && sizeSelect.value) {
        return sizeSelect.value;
      }

      // 4. Check all dropdown selects
      const selects = section.querySelectorAll('select');
      for (const select of selects) {
        const name = (select.name || '').toLowerCase();
        const parentInput = select.closest('.product-form__input, fieldset');
        const label = parentInput ? (parentInput.querySelector('label, legend')?.textContent || '').toLowerCase() : '';

        if (name.includes('size') || label.includes('size') || this.isCustomTailoredValue(select.value)) {
          return select.value;
        }
      }

      // 5. Check active swatch/label or variant buttons
      const activeSwatches = section.querySelectorAll('.varient-class input[type="radio"]:checked, .swatch-element.active, label.active');
      for (const el of activeSwatches) {
        const val = el.value || el.textContent || '';
        if (this.isCustomTailoredValue(val)) {
          return val;
        }
      }

      // 6. Check variant-radios / variant-selects options if available
      const variantRadios = section.querySelector('variant-radios, variant-selects');
      if (variantRadios && Array.isArray(variantRadios.options) && variantRadios.options.length > 0) {
        for (const opt of variantRadios.options) {
          if (this.isCustomTailoredValue(opt)) {
            return opt;
          }
        }
        return variantRadios.options[0];
      }

      return null;
    }

    onSizeChange(event) {
      if (event && event.target && this.contains(event.target)) return;
      setTimeout(() => {
        this.checkCurrentSize();
      }, 0);
    }

    checkCurrentSize() {
      const isCustom = this.isCustomSelected();

      if (isCustom) {
        // Custom Size selected -> Form VISIBLE & Mandatory
        this.removeAttribute('hidden');
        this.style.display = 'block';
        this.setAttribute('data-mandatory', 'true');
        if (this.optionalLabel) {
          this.optionalLabel.style.display = 'none';
        }
        this.syncHiddenProperties();
      } else {
        // Predefined Size selected (XS, S, M, L, XL, XXL, XXXL) -> Form HIDDEN
        this.setAttribute('hidden', '');
        this.style.display = 'none';
        this.setAttribute('data-mandatory', 'false');
        this.hideError();
        this.disableAllProperties();
      }
    }

    validateCustomMeasurements(showError = true) {
      const isCustom = this.isCustomSelected();
      // If predefined size, measurements are optional -> always valid
      if (!isCustom) {
        this.hideError();
        return { valid: true };
      }

      let firstInvalidInput = null;
      let hasError = false;

      this.inputs.forEach((input) => {
        const rawVal = input.value ? input.value.trim() : '';
        const wrapper = input.closest('.custom-measurement-input-wrapper');
        // Check if numeric (supports integer and decimals e.g. 34, 28.5)
        const isNumeric = rawVal.length > 0 && !isNaN(parseFloat(rawVal)) && isFinite(rawVal) && parseFloat(rawVal) > 0;

        if (!isNumeric) {
          hasError = true;
          if (!firstInvalidInput) {
            firstInvalidInput = input;
          }
          if (showError && wrapper) {
            wrapper.classList.add('is-invalid');
          }
        } else {
          if (wrapper) {
            wrapper.classList.remove('is-invalid');
          }
        }
      });

      if (hasError) {
        if (showError) {
          this.showError('Please enter all measurements to continue.');
          if (firstInvalidInput) {
            firstInvalidInput.focus();
            if (typeof this.scrollIntoView === 'function') {
              this.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
        return { valid: false, firstInvalidInput };
      }

      this.hideError();
      this.syncHiddenProperties();
      return { valid: true };
    }

    showError(msg = 'Please enter all measurements to continue.') {
      if (this.errorElement) {
        this.errorElement.textContent = msg;
        this.errorElement.classList.add('is-visible');
        this.errorElement.style.display = 'block';
      }
    }

    hideError() {
      if (this.errorElement) {
        this.errorElement.classList.remove('is-visible');
        this.errorElement.style.display = 'none';
      }
      this.inputs.forEach((input) => {
        const wrapper = input.closest('.custom-measurement-input-wrapper');
        if (wrapper) wrapper.classList.remove('is-invalid');
      });
    }

    onActionAttempt(event) {
      const target = event.target;
      if (!target || !(target instanceof Element)) return;

      const isAddToCart = target.closest('button[name="add"], .product-form__submit, button.product-form__submit');
      const isBuyNow = target.closest('.shopify-payment-button, .shopify-payment-button__button, .shopify-payment-button__button--unbranded, [data-testid="Checkout-button"]');
      const isWishlist = target.closest('.product-form__wishlist-row a, .product-form__wishlist-row button, .action--wishlist, .add-wishlist, .wishlist-btn, .vtn-wishlist-button-block button, .dT_VProdWishList a');

      if (isAddToCart || isBuyNow || isWishlist) {
        // Only guard actions belonging to this product / section
        const section = this.closest('section') || (this.dataset.sectionId ? document.getElementById(`shopify-section-${this.dataset.sectionId}`) : null);
        if (section && !section.contains(target)) return;

        const isCustom = this.isCustomSelected();
        if (isCustom) {
          const validation = this.validateCustomMeasurements(true);
          if (!validation.valid) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
          }
        }
      }
    }

    formatMeasurementValue(val) {
      if (!val) return '';
      const trimmed = val.trim();
      if (!trimmed) return '';
      if (/(\bin|\binches|in\.|"|cm)$/i.test(trimmed)) {
        return trimmed;
      }
      return `${trimmed} in`;
    }

    onInputChange(event) {
      if (event && event.target && event.target instanceof Element) {
        const input = event.target;
        const val = input.value ? input.value.trim() : '';
        const isNumeric = val.length > 0 && !isNaN(parseFloat(val)) && isFinite(val) && parseFloat(val) > 0;
        const wrapper = input.closest('.custom-measurement-input-wrapper');
        if (isNumeric && wrapper) {
          wrapper.classList.remove('is-invalid');
        }
      }

      // Check if all fields are valid to auto-clear error message
      const allValid = Array.from(this.inputs).every((inp) => {
        const val = inp.value ? inp.value.trim() : '';
        return val.length > 0 && !isNaN(parseFloat(val)) && isFinite(val) && parseFloat(val) > 0;
      });

      if (allValid) {
        this.hideError();
      }

      this.syncHiddenProperties();
    }

    syncHiddenProperties() {
      this.inputs.forEach((input) => {
        const propName = input.dataset.propertyName;
        const rawValue = input.value ? input.value.trim() : '';
        const hiddenInput = this.querySelector(`input[data-hidden-property="${propName}"]`);

        if (hiddenInput) {
          if (rawValue.length > 0 && !isNaN(parseFloat(rawValue))) {
            hiddenInput.value = this.formatMeasurementValue(rawValue);
            hiddenInput.removeAttribute('disabled');
            hiddenInput.disabled = false;
          } else {
            hiddenInput.value = '';
            hiddenInput.setAttribute('disabled', 'disabled');
            hiddenInput.disabled = true;
          }
        }
      });
    }

    disableAllProperties() {
      this.hiddenInputs.forEach((hiddenInput) => {
        hiddenInput.setAttribute('disabled', 'disabled');
        hiddenInput.disabled = true;
      });
    }
  }

  customElements.define('custom-measurement-form', CustomMeasurementForm);
}
